import { MutableRefObject, ReactElement } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faXmark,
  faCircleExclamation,
} from "@fortawesome/sharp-regular-svg-icons";

import "@/styles/components/shared/inline-banner.scss";
import _ from "lodash";
import classNames from "classnames";

type InlineBannerPropsWithStringMessage = {
  message: string;
  onDismiss?: (message: string) => void;
};

type InlineBannerPropsWithJSXMessage = {
  message: ReactElement;
  onDismiss?: () => void;
};

type InlineBannerProps = {
  type?: string;
  dismissable?: boolean;
  ref?: MutableRefObject<HTMLDivElement | null>;
  className?: string;
} & (InlineBannerPropsWithStringMessage | InlineBannerPropsWithJSXMessage);

function InlineBanner({
  message,
  type,
  dismissable,
  onDismiss,
  ref,
  className,
}: InlineBannerProps) {
  // type = default, information, success, error

  const navigate = useNavigate();
  const location = useLocation();

  const handleDismiss = () => {
    if (onDismiss) {
      if (typeof message === "string") {
        const onDismissWithMessage =
          onDismiss as Required<InlineBannerPropsWithStringMessage>["onDismiss"];

        onDismissWithMessage(message);
      } else {
        const onDismissWithoutMessage =
          onDismiss as Required<InlineBannerPropsWithJSXMessage>["onDismiss"];

        onDismissWithoutMessage();
      }
    }

    // Clear the query parameter without navigating, in case a message was sent via get string
    const urlWithoutQuery = location.pathname;
    navigate(urlWithoutQuery);
  };

  const dismissalTypes = ["error", "success", "information"];

  const warningTypes = ["error", "information"];

  const iconElement = (iconType?: string) => {
    if (_.includes(warningTypes, iconType)) {
      return (
        <FontAwesomeIcon
          className="close_button"
          onClick={handleDismiss}
          icon={faCircleExclamation}
        />
      );
    }

    if (iconType === "success") {
      return (
        <FontAwesomeIcon
          className="close_button"
          onClick={handleDismiss}
          icon={faCheck}
          fill="#349b6f"
        />
      ); // GREEN
    }

    return (
      <FontAwesomeIcon
        className="close_button"
        onClick={handleDismiss}
        icon={faCheck}
        fill="#FFF"
      />
    );
  };

  const dismissalIconElement = (iconType?: string) => {
    if (_.includes(dismissalTypes, iconType)) {
      return <FontAwesomeIcon onClick={handleDismiss} icon={faXmark} />;
    }
    return (
      <FontAwesomeIcon onClick={handleDismiss} icon={faXmark} fill="#FFF" />
    ); // WHITE
  };

  return (
    <div
      ref={ref}
      className={classNames(
        "centered_large_outer_section",
        "banner",
        type,
        {
          dismissable,
        },
        className,
      )}
    >
      <div className="banner_icon">{iconElement(type)}</div>
      <div className="banner_message">{message}</div>
      <div className="banner_dismiss">
        {dismissable && dismissalIconElement(type)}
      </div>
    </div>
  );
}

export default InlineBanner;
